import { useCallback, useRef } from 'react';
import { useUtilisateurService } from '@europrocurement/l2d-domain';
import { User, useUsersCtx, useUsersCtxDispatch } from '../providers/UsersProvider';

export const useResolveUserByKeycloakId = () => {
    const { getUserFiltered } = useUsersCtx();
    const dispatch = useUsersCtxDispatch();
    const { utilisateurApi } = useUtilisateurService();
    const inflightRef = useRef<Map<string, Promise<User | null>>>(new Map());

    // Stable refs to avoid recreating resolveUserByKeycloakId on every context update
    const getUserFilteredRef = useRef(getUserFiltered);
    getUserFilteredRef.current = getUserFiltered;
    const utilisateurApiRef = useRef(utilisateurApi);
    utilisateurApiRef.current = utilisateurApi;
    const dispatchRef = useRef(dispatch);
    dispatchRef.current = dispatch;

    const resolveUserByKeycloakId = useCallback(
        async (keycloakId: string): Promise<User | null> => {
            const cached = getUserFilteredRef.current({ keycloakUserIdentifier: keycloakId });
            if (cached) return cached;

            if (inflightRef.current.has(keycloakId)) {
                return inflightRef.current.get(keycloakId)!;
            }

            const promise = utilisateurApiRef.current
                .apiUtilisateursGetCollection({ userIdentifier: keycloakId })
                .then((response) => {
                    const raw = response.data['hydra:member'][0] ?? null;
                    if (!raw) return null;

                    const user: User = {
                        id: raw.id as number,
                        firstName: raw.prenom as string,
                        lastName: raw.nom as string,
                        keycloakUserIdentifier: raw.userIdentifier ?? undefined,
                        mail: raw.mail as string,
                        deleted: raw.isDeleted as boolean,
                        formalist: raw.isFormalist as boolean,
                        hasAccessL2DF: raw.hasAccessL2DF as boolean,
                        service: {
                            id: raw?.service?.id as number,
                            label: raw?.service?.libelle as string,
                        },
                        workGroups: (raw?.groupeTravail || []).map((group) => ({
                            id: group.id as number,
                            label: group.lib as string,
                        })),
                    };

                    dispatchRef.current?.({ type: 'add_user', payload: user });
                    return user;
                })
                .catch(() => null)
                .finally(() => {
                    inflightRef.current.delete(keycloakId);
                });

            inflightRef.current.set(keycloakId, promise);
            return promise;
        },
        [],
    ); // stable — no deps, reads via refs

    return { resolveUserByKeycloakId };
};
